import React from 'react';
import { isChunkLoadError } from '../utils/lazyWithRetry.js';

/**
 * Catches render-time errors so one broken component can't blank the whole app.
 *
 * React 18 unmounts the entire tree on an uncaught render error. With no
 * boundary, a single bad value — e.g. `.map()` on a paginated `{rows, total}`
 * envelope that was expected to be an array — turned into a white screen across
 * the whole console instead of one broken panel (the QA "admin page is blank"
 * report). This keeps the failure local and, crucially, visible: a white page
 * gives a user nothing to report, while this shows what broke.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the stack in the console for support/debugging — the UI stays terse.
    console.error('[ErrorBoundary]', error, info?.componentStack);
  }

  handleReload = () => {
    this.setState({ error: null });
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    // A stale code-split chunk (a deploy landed under this tab) is not a bug in
    // the page — lazyWithRetry has already spent its one automatic reload, so
    // all that is left is to say plainly what happened. The raw
    // "Failed to fetch dynamically imported module" tells a user nothing.
    const staleBuild = isChunkLoadError(this.state.error);

    return (
      <div className="p-4" role="alert" data-testid="error-boundary">
        <div className="surface-card mx-auto" style={{ maxWidth: 520 }}>
          <h2 className="h5 fw-bold mb-2">
            {staleBuild ? 'A new version of the app was released' : 'Something went wrong on this page'}
          </h2>
          <p className="text-muted small mb-3">
            {staleBuild
              ? 'This tab is still running the previous version. Reload to pick up the update — nothing you entered on other pages is affected.'
              : 'The rest of the app is still available. If this keeps happening, quote the details below to support.'}
          </p>
          <pre
            className="small text-muted mb-3"
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            data-testid="error-boundary-detail"
          >
            {String(this.state.error?.message || this.state.error)}
          </pre>
          <button type="button" className="btn btn-primary btn-sm" onClick={this.handleReload}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}
